import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation, useSearchParams } from 'react-router-dom';
import NavItem from './NavItem';
import Button from '../reusable/Button/Button';
import ShareDialog from './ShareDialog';
import { deauthenticate } from '../store/api/apiSlice';
import { useGetAccountsQuery } from '../store/api/dashboardApi';
import { selectUser } from '../store/slices/authSlice';
import { selectPickedAccount } from '../store/slices/accountSlice';
import { PROFILE } from '../pages/Profile/data';
import { displayNameOf } from '../utilities/user';
import { useGetProfileDetailsQuery } from '../store/api/profileApi';
import { GROUPS } from './nav';
import { byGates, useNavGates } from './useNavGates';
import { t } from '../i18n/i18n';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The sub-nav belonging to each group row, keyed by the row's own route.
 *
 * On the rail a group cannot unfold — there is no width for a nested list — so
 * hovering it opens the children beside it instead. The lookup is by `to`
 * because that is what the hovered element can tell us: the rows are anchors,
 * and `a.pathname` gives the route back without a second data attribute to keep
 * in step with `nav.js`.
 */
const SUBNAV = new Map(
  GROUPS.flatMap((group) => group.items)
    .filter((item) => item.items?.length)
    .map((item) => [item.to, item.items]),
);

export default function Sidebar({ collapsed = false, onToggle }) {
  const gates = useNavGates();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const account = useRef(null);
  const dispatch = useDispatch();
  /** The profile's name, so an edit in the admin or Profile shows here too. */
  const user = useSelector(selectUser);
  const { data: profile } = useGetProfileDetailsQuery(undefined, { skip: !user });
  const name = displayNameOf(user, PROFILE.firstName, profile);
  const email = user?.email ?? PROFILE.email;

  const { pathname } = useLocation();
  const nav = useRef(null);
  const promo = useRef(null);
  /** The promo card's height plus the footer gap it brings, kept while it's folded. */
  const promoRoom = useRef(0);
  /**
   * Whether the menu needs the promo card's space (TI review N68): on a short
   * window — or one with Payout & Certificates open — the card left the nav a
   * thin scrolling strip under it. Folded while the menu overflows, and only
   * unfolded once the menu would still fit with the card back, so the two
   * thresholds can't chase each other.
   */
  const [cramped, setCramped] = useState(false);


  const { data: accountList } = useGetAccountsQuery({ page: 1, page_size: 100 });
  const accounts = accountList?.accounts?.results ?? [];
  const [params] = useSearchParams();
  const picked = useSelector(selectPickedAccount);
  const shareAccountId = params.get('login') ?? picked.loginId ?? accounts[0]?.login_id ?? null;


  const [fill, setFill] = useState(null);
  const [smooth, setSmooth] = useState(false);

  const measure = useCallback(() => {
    const box = nav.current;
    const row = box?.querySelector('.nav-item.is-active');
    if (!box || !row) {
      setFill(null);
      return;
    }

    const navBox = box.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();

    const next = {
      // Plus the scroll offset: the fill is a child of the scrolling content,
      // so it is placed in content space, not in the visible window of it.
      top: rowBox.top - navBox.top + box.scrollTop,
      left: rowBox.left - navBox.left + box.scrollLeft,
      width: rowBox.width,
      height: rowBox.height,
    };

    setFill((prev) => {
      if (
        prev &&
        Math.abs(prev.top - next.top) < 0.5 &&
        Math.abs(prev.left - next.left) < 0.5 &&
        Math.abs(prev.width - next.width) < 0.5 &&
        Math.abs(prev.height - next.height) < 0.5
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  // Before paint, so the fill is never seen at a stale position.
  useLayoutEffect(measure, [measure, pathname]);

  /* Folding to the rail resizes and moves every row continuously for 360ms,
     so easing to a target that is itself still moving would read as drift —
     the fill drops its transition and tracks the active row frame by frame
     instead, taking it back when the fold lands.

     A timer ends the follow, not the frame loop itself. rAF is the right tool
     for tracking something mid-animation but the wrong one to depend on for
     finishing: it is throttled to a standstill in a hidden tab, and a loop
     that ends only when a frame says so would leave the fill transitionless
     for as long as the tab stayed in the background. Timers still fire there.

     This also runs on mount, which is what keeps the fill from sliding in from
     the top of the nav on load: it is placed by the effect above before the
     first paint, and only earns its transition once this settles. */
  useLayoutEffect(() => {
    setSmooth(false);

    let frame = requestAnimationFrame(function step() {
      measure();
      frame = requestAnimationFrame(step);
    });

    const settle = setTimeout(() => {
      cancelAnimationFrame(frame);
      measure();
      setSmooth(true);
    }, 460);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
    };
  }, [collapsed, measure]);

  useEffect(() => {
    const list = nav.current;
    const card = promo.current;
    if (!list || !card || typeof ResizeObserver === 'undefined') return undefined;

    const check = () =>
      setCramped((folded) => {
        if (!folded) {
          // 20px: the footer's gap, which the folded card gives back too.
          if (card.offsetHeight) promoRoom.current = card.offsetHeight + 20;
          return list.scrollHeight > list.clientHeight + 1;
        }
        return list.scrollHeight > list.clientHeight - promoRoom.current;
      });

    // The nav's own box (the window, the card folding) and every group in it
    // (a sub-list opening) — either can change whether the menu fits.
    const observer = new ResizeObserver(check);
    observer.observe(list);
    Array.from(list.children).forEach((child) => observer.observe(child));
    check();
    return () => observer.disconnect();
  }, []);

  /* A group folding moves whatever sits below it. That only matters on the way
     out — a group is open while its own row is current, and that row sits
     above the sub-list, so expanding never moves it. Leaving one does: the
     list collapses under the row that just became current. One re-measure when
     the fold lands is enough, and the fill keeps its transition so it eases
     the last few pixels into place rather than snapping. */
  useEffect(() => {
    const el = nav.current;
    if (!el) return undefined;

    const onEnd = (event) => {
      if (event.propertyName === 'grid-template-rows') measure();
    };

    el.addEventListener('transitionend', onEnd);
    return () => el.removeEventListener('transitionend', onEnd);
  }, [measure]);

  /* On the 76px rail the nav rows are unlabelled glyphs — the label is still
     in the DOM for screen readers, but a sighted user has nothing to read. A
     tooltip cannot live inside `.sidebar__nav`: that element scrolls, so any
     child would be clipped at the rail's edge. It is rendered at the sidebar
     root and positioned from the hovered row's rect instead.

     A row that owns a sub-nav gets the same treatment carried one step further:
     rather than a label it opens its children, so Certificates is one hover and
     one click away without expanding the rail and collapsing it again. Both are
     the same piece of state — one anchor, one lifetime — because they are the
     same gesture answered with more or less detail. */
  const [tip, setTip] = useState(null);

  /* A submenu is a hover target of its own, and there is a gap between it and
     the row that opened it. Closing the moment the pointer leaves the row would
     shut it while the user is still on their way in, so leaving only *schedules*
     the close and entering either the row or the panel calls it off. */
  const closeTimer = useRef(null);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setTip(null), 140);
  }, [cancelClose]);

  const closeNow = useCallback(() => {
    cancelClose();
    setTip(null);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Nothing to label once the full sidebar is back — and the rows unfold their
  // own sub-navs again, so the menu would be a second copy of them.
  useEffect(() => {
    if (!collapsed) closeNow();
  }, [collapsed, closeNow]);

  /* Escape closes the submenu. It is a menu with focusable links in it, so it
     needs the same way out as the account menu below rather than only
     unhovering — a keyboard user who has tabbed into it has no pointer to move
     away. */
  useEffect(() => {
    if (!tip?.items) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeNow();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [tip, closeNow]);

  const peek = (event) => {
    if (!collapsed) return;
    const row = event.target.closest?.('.nav-item');
    const label = row?.dataset.label;
    if (!label) return;

    cancelClose();
    const rect = row.getBoundingClientRect();

    /* The rows are 52px centred in a 76px rail, so a row's own right edge sits
       12px *inside* the sidebar. The tooltip is small and click-through and has
       always hung off the row; a menu is neither, and starting it there would
       lay a 176px panel over the rail it belongs to. It clears the sidebar
       instead. */
    const railRight = row.closest('.sidebar')?.getBoundingClientRect().right;

    setTip({
      label,
      // `undefined` for a leaf row, which is what makes it render as a plain
      // label rather than a menu.
      items: SUBNAV.get(row.pathname),
      x: rect.right + 10,
      menuX: (railRight ?? rect.right) + 8,
      // A label is centred on its row; a menu hangs from the row's top edge so
      // its first child lines up with the icon that opened it. Both are kept so
      // the renderer can pick without measuring again.
      y: rect.top + rect.height / 2,
      top: rect.top,
    });
  };

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onPointerDown = (event) => {
      if (!account.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const signOut = () => {
    setMenuOpen(false);
    dispatch(deauthenticate());
  };

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--rail' : ''}`}>
      <div className="sidebar__brand">
        <NavLink to="/dashboard" className="sidebar__logos" aria-label={t('Go to dashboard')}>
          <span className="icon sidebar__logo" style={icon('brand-alphacapital')} />
          <span className="icon sidebar__mark" style={icon('brand-mark')} />
        </NavLink>
        <button
          type="button"
          className="sidebar__collapse"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('Expand sidebar') : t('Collapse sidebar')}
          onClick={onToggle}
        >
          <span className="icon" style={icon('sidebar-collapse')} />
        </button>
      </div>

      <div className="sidebar__rule" />

      {/* Pointer and keyboard both, so tabbing the rail reads the same as
          hovering it. `onMouseOut`/`onBlur` bubble where the non-React
          `mouseleave`/`focusout` pair would not. */}
      <nav
        className={`sidebar__nav${fill ? ' has-fill' : ''}`}
        ref={nav}
        onMouseOver={peek}
        onMouseOut={scheduleClose}
        onFocus={peek}
        onBlur={scheduleClose}
        // A row that has scrolled is no longer under the panel anchored to it.
        onScroll={closeNow}
      >
        {/* Inside the scrolling content, so it moves with the rows it marks. */}
        {fill && (
          <span
            className={`nav-fill${smooth ? ' is-smooth' : ''}`}
            style={{
              transform: `translate3d(${fill.left}px, ${fill.top}px, 0)`,
              width: `${fill.width}px`,
              height: `${fill.height}px`,
            }}
            aria-hidden="true"
          />
        )}

        {GROUPS.map((group, i) => (
          <div className="sidebar__group" key={group.label ?? i}>
            {group.label && <div className="sidebar__label">{t(group.label)}</div>}
            {group.items.filter(byGates(gates)).map((item) => (
              <NavItem
                key={item.to}
                {...item}
                end={item.section ? false : (item.end ?? true)}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* A group's children, opened beside the rail instead of underneath the
          row — on 76px there is no width to unfold into, and expanding the whole
          sidebar to reach Certificates only to collapse it again is the detour
          this removes.

          Real links, not a decorative panel: this is the only way to reach a
          sub-route while the rail is up, since `.sidebar--rail` hides the row's
          own `.nav-group__sub` outright (`visibility: hidden` takes it out of
          the tab order too, so these do not become a second set of stops). */}
      {collapsed && tip?.items &&
        createPortal(
          <div
            className="rail-menu"
            style={{ top: `${tip.top}px`, left: `${tip.menuX}px` }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onFocus={cancelClose}
            onBlur={scheduleClose}
          >
            <div className="rail-menu__title">{tip.label}</div>
            {tip.items.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                end={child.end ?? true}
                onClick={closeNow}
                className={({ isActive }) => `rail-menu__item${isActive ? ' is-active' : ''}`}
              >
                {t(child.label)}
              </NavLink>
            ))}
          </div>,
          document.body,
        )}

      {/* Decorative: `aria-hidden` because the row's own label is already in
          the accessibility tree, folded to zero width rather than removed.
          Only for rows with nothing to open — a group shows the menu above,
          which carries the same label as its heading. */}
      {collapsed && tip && !tip.items &&
        createPortal(
          <div
            className="rail-tip"
            style={{ top: `${tip.y}px`, left: `${tip.x}px` }}
            aria-hidden="true"
          >
            {tip.label}
          </div>,
          document.body,
        )}

      <div className="sidebar__footer">
        {/* The wrapper is the animated part, not the card: it folds its grid
            track to 0fr on the rail so the promo collapses with the rest of
            the sidebar instead of disappearing on the first frame. Same shape
            as the nav's sub-lists — see NavItem.scss. */}
        <div className={`sidebar__promo${cramped ? ' is-folded' : ''}`} ref={promo}>
          <div className="academy">
            {/* MyAlpha Quant — the trading journal, replacing the Divento
                Academy promo. Logo and line are the product's own (its sign-up
                page); it opens in a new tab, being another site. */}
            <span className="icon academy__logo academy__logo--myalpha" style={icon('myalpha-quant')} />
            <p className="academy__copy">
              {t(
                'The end of blind trading. See what works, optimize what doesn’t, and trade with real confidence.',
              )}
            </p>
            <Button
              variant="light"
              href="https://app.myalphaquant.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Open MyAlpha Quant')}
            </Button>
          </div>
        </div>

        <div className="sidebar__rule" style={{ margin: 0 }} />

        <div className={`account${menuOpen ? ' is-open' : ''}`} ref={account}>
          <button
            type="button"
            className="user"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="user__avatar">{name.charAt(0).toUpperCase()}</span>
            <span className="user__meta">
              <span className="user__name">{name}</span>
              <span className="user__email">{email}</span>
            </span>
            <span className="icon user__caret" style={icon('user-updown')} />
          </button>

          <div className="account__menu" role="menu" inert={!menuOpen}>
            <NavLink
              to="/notifications"
              className="account__link"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
            >
              {t('Notifications')}
            </NavLink>
            <NavLink
              to="/profile"
              className="account__link"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
            >
              {t('Profile')}
            </NavLink>
            {/* The sheet, not a link to the page: the account has to be
                published before `/p/:accountId` resolves for anyone, and
                that switch lives in here alongside the link it makes work. */}
            <button
              type="button"
              className="account__link"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                setIsShareOpen(true);
              }}
            >
              {t('Public Page')}
            </button>
            <NavLink
              to="/help-centre"
              className="account__link"
              role="menuitem"
              onClick={() => setMenuOpen(false)}
            >
              {t('Help centre')}
            </NavLink>

            <div className="account__divider" />

            <button
              type="button"
              className="account__link account__link--danger"
              role="menuitem"
              onClick={signOut}
            >
              {t('Log out')}
            </button>
          </div>
        </div>
      </div>

      {/* Beside the menu, not in it: the menu's rows are staggered open by
          `nth-child`, which counts every child it is given. */}
      <ShareDialog
        open={isShareOpen}
        accountId={shareAccountId}
        account={accounts.find((a) => a.login_id === shareAccountId)}
        onClose={() => setIsShareOpen(false)}
      />
    </aside>
  );
}
